import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowLeft, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../layout/AuthLayout';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

function VerifyCode() {
  const { t } = useTranslation();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);
  const { login } = useAuth();

  // Get email from location state or redirect to signup
  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendCountdown]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      setError(t('verifyCode.completeCodeError'));
      setIsLoading(false);
      return;
    }

    console.log('Sending verification request:', {
      email: email,
      code: parseInt(verificationCode, 10)
    });

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          code: parseInt(verificationCode, 10)
        })
      });

      const result = await response.json();
      
      // Handle HTTP errors
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      console.log('Verification response:', result);

      // Check if verification was successful based on response
      if (result.valid === true) {
        // If verification includes token and user data, automatically log in
        if (result.token && result.user) {
          const userData = {
            name: result.user?.name || result.user?.username || result.username || 'User',
            email: result.user?.email || email,
            role: result.user?.role || 'User'
          };
          
          login(result.token, userData);
          
          // Redirect to home page
          navigate('/', { replace: true });
        } else {
          // Fallback: redirect to sign in page with pre-filled email and success message
          navigate('/signin', { 
            state: { 
              message: t('verifyCode.successMessage'),
              email: email
            } 
          });
        }
      } else {
        // Show error message from backend
        throw new Error(result.message || 'Verification failed');
      }

    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError(t('common.serverConnectionError'));
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendDisabled(true);
    setResendCountdown(30);
    setError('');

    try {
      await fetch(`${API_URL}/api/auth/verify-email/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email
        })
      });

      // Just send the request, don't expect a response
      console.log('Verification code resent successfully');
      
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError(t('common.serverConnectionError'));
      } else {
        setError(t('verifyCode.resendError'));
      }
      setResendDisabled(false);
      setResendCountdown(0);
    }
  };

  const isFormValid = code.every(digit => digit !== '') && code.length === 6;

  return (
    <AuthLayout 
      title={t('verifyCode.title')}
      subtitle={t('verifyCode.subtitle', { email })}
    >
      {/* Form Card */}
      <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Verification Code Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
              {t('verifyCode.enterCode')}
            </label>
            <div className="flex justify-center space-x-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary"
                  placeholder=""
                />
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-primary hover:bg-blue-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-primary disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isLoading ? t('verifyCode.verifying') : t('verifyCode.verifyEmail')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        </form>

        {/* Resend Code Section */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-3">
            {t('verifyCode.didntReceiveCode')}
          </p>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resendDisabled}
            className="inline-flex items-center text-sm text-blue-primary hover:text-blue-secondary font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            {resendDisabled 
              ? t('verifyCode.resendIn', { countdown: resendCountdown }) 
              : t('verifyCode.resendCode')
            }
          </button>
        </div>

        {/* Back to Sign Up Link */}
        <div className="mt-6 text-center">
          <Link
            to="/signup"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 font-medium"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('verifyCode.backToSignup')}
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}

export default VerifyCode;
